import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { merge } from 'rxjs';
import { tap, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import {
  BibliotecaInsumoArquivo,
  BibliotecaArquivoFiltros,
} from 'src/app/_models/biblioteca-arquivos';
import { BibliotecaArquivosService } from 'src/app/_services/biblioteca-arquivos.service';
import { InsumoService } from 'src/app/_services/insumos.service';
import { AccountService } from 'src/app/_services';

@Component({
  selector: 'app-biblioteca-arquivos-insumos',
  templateUrl: './biblioteca-arquivos-insumos.component.html',
  styleUrls: ['./biblioteca-arquivos-insumos.component.scss'],
})
export class BibliotecaArquivosInsumosComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  displayedColumns: string[] = [
    'originalFileName',
    'insumoName',
    'insumoSegment',
    'insumoCompetence',
    'competenceStatus',
    'userUploadFullName',
    'createdAt',
    'version',
    'actions',
  ];

  dataSource: BibliotecaInsumoArquivo[] = [];
  filtrosForm: FormGroup;
  loading = false;
  dataSourceLength = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50, 100];
  currentUser: any;
  paginacaoConfigurada = false;

  segmentos: string[] = [];
  status: string[] = [];
  competencias: string[] = [];

  constructor(
    private bibliotecaService: BibliotecaArquivosService,
    private insumoService: InsumoService,
    private accountService: AccountService,
    private formBuilder: FormBuilder,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.currentUser = this.accountService.userValue;
  }

  ngOnInit(): void {
    this.inicializarFormulario();
    this.carregarOpcoesDeFiltros();
  }

  ngAfterViewInit(): void {
    this.filtrosForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.filtrar();
      });

    this.carregarArquivos();
  }

  configurarPaginacao(): void {
    if (this.paginacaoConfigurada) {
      return;
    }

    if (this.sort && this.paginator) {
      this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.carregarArquivos()))
        .subscribe();

      this.paginacaoConfigurada = true;
    }
  }

  inicializarFormulario(): void {
    this.filtrosForm = this.formBuilder.group({
      fileName: [''],
      uploaderEmail: [''],
      uploaderName: [''],
      segment: ['TODOS'],
      status: ['TODOS'],
      competence: ['TODOS'],
      startDate: [''],
      endDate: [''],
    });
  }

  carregarOpcoesDeFiltros(): void {
    // Carregar segmentos únicos
    this.segmentos = [
      'B2B',
      'B2C',
      'TODOS',
    ];

    this.status = [
      'VIGENTE',
      'ENCERRADO',
    ];

    // Carregar competências (últimos 12 meses)
    const competencias = [];
    const hoje = new Date();
    for (let i = 0; i < 12; i++) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();
      competencias.push(`${mes}/${ano}`);
    }
    this.competencias = competencias;
  }

  carregarArquivos(): void {
    this.loading = true;

    const page = this.paginator ? this.paginator.pageIndex : 0;
    const size = this.paginator ? this.paginator.pageSize : this.pageSize;
    const sortField = this.sort?.active || 'createdAt';
    const sortOrder = this.sort?.direction || 'desc';

    const formValues = this.filtrosForm.value;
    const filtros = {
      fileName: formValues.fileName || '',
      uploaderEmail: formValues.uploaderEmail || '',
      uploaderName: formValues.uploaderName || '',
      segment: formValues.segment === 'TODOS' ? '' : formValues.segment,
      status: formValues.status === 'TODOS' ? '' : formValues.status,
      competence: formValues.competence === 'TODOS' ? '' : formValues.competence,
      startDate: formValues.startDate || '',
      endDate: formValues.endDate || '',
    };

    this.bibliotecaService
      .getInsumoArquivosPageable(page, size, sortField, sortOrder, filtros)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (response) => {
          this.dataSource = response.content || [];
          this.dataSourceLength = response.totalElements || 0;

          setTimeout(() => {
            this.configurarPaginacao();
          });
        },
        (error) => {
          console.error('Erro ao carregar arquivos:', error);
          this.snackBar.open('Não foi possível carregar os arquivos.', 'Fechar', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
          this.dataSource = [];
          this.dataSourceLength = 0;
        }
      );
  }

  filtrar(): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.carregarArquivos();
  }

  limparFiltros(): void {
    this.filtrosForm.reset({
      fileName: '',
      uploaderEmail: '',
      uploaderName: '',
      segment: 'TODOS',
      status: 'TODOS',
      competence: 'TODOS',
      startDate: '',
      endDate: '',
    });
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.carregarArquivos();
  }

  downloadArquivo(arquivo: BibliotecaInsumoArquivo): void {
    this.loading = true;
    this.insumoService
      .downloadNotification(
        this.currentUser.id,
        arquivo.insumoId,
        arquivo.anexoId
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (response) => {
          const link = document.createElement('a');
          link.href = `${response.url}`;
          link.download = arquivo.originalFileName;
          link.target = '_blank';
          link.click();

          this.snackBar.open('Download iniciado com sucesso!', 'Fechar', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-success']
          });
        },
        (error) => {
          console.error('Erro ao fazer download:', error);
          this.snackBar.open('Não foi possível fazer o download do arquivo.', 'Fechar', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
        }
      );
  }

  visualizarInsumo(insumoId: number): void {
    this.router.navigate(['/insumos/visualizar', insumoId]);
  }

  exportarParaExcel(): void {
    this.loading = true;
    const filtros: BibliotecaArquivoFiltros = this.filtrosForm.value;

    this.bibliotecaService
      .exportInsumoArquivosToExcel(filtros)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `biblioteca-arquivos-insumos-${new Date().getTime()}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);

          this.snackBar.open('Exportação concluída com sucesso!', 'Fechar', {
            duration: 3000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-success']
          });
        },
        (error) => {
          console.error('Erro ao exportar:', error);
          this.snackBar.open('Não foi possível exportar os dados.', 'Fechar', {
            duration: 5000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
        }
      );
  }

  formatarData(timestamp: string): string {
    if (!timestamp) return '-';
    const data = new Date(timestamp);
    return data.toLocaleDateString('pt-BR') + ' ' + data.toLocaleTimeString('pt-BR');
  }

  getStatusClass(status: string): string {
    const statusClasses = {
      '0': 'badge-success',
      '1': 'badge-secondary',
      'VIGENTE': 'badge-success',
      'ENCERRADO': 'badge-secondary',
    };
    return statusClasses[status] || 'badge-secondary';
  }

  getStatusLabel(status: string): string {
    const statusLabels = {
      '0': 'Vigente',
      '1': 'Encerrado',
      'VIGENTE': 'Vigente',
      'ENCERRADO': 'Encerrado',
    };
    return statusLabels[status] || status;
  }
}
